import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Directions, GoogleStreetViewImageService } from 'app/shared/google/google-street-view-image.service';
import { Point } from 'app/shared/ll/models/geometry';
import { from, mergeMap } from 'rxjs';
import { UiModule } from '../ui';
import { GoogleLoader } from './google-loader.service';
import { GOOGLE_API_KEY } from './google.tokens';


@Component({
    selector: 'google-street-view-interactive',
    imports: [
        UiModule,
    ],
    styles: [
        `
        .panorama {
            height: 100%;
        }
        `,
    ],
    template: `
        @if (showError()) {
            <ui-alert type="info">Can't find property on Google Street View.</ui-alert>
        }
        @else {
            <div class="panorama" #panoEl></div>
        }
    `,
})
export class GoogleStreetViewInteractiveComponent implements AfterViewInit {

    showError = signal(false);

    center = input.required<Point>();

    fullAddress = input.required<string>();

    private panoEl = viewChild.required<ElementRef>('panoEl');

    panoramaChange = output<void>();

    private pano: google.maps.StreetViewPanorama;

    private googleApiKey = inject(GOOGLE_API_KEY);

    private gLoader = inject(GoogleLoader);

    private gStreetViewImageService = inject(GoogleStreetViewImageService);

    private destroyRef = inject(DestroyRef);

    ngAfterViewInit() {
        const createPanorama = (directions: Directions | null) => new Promise<{ pano: string | null; heading: number }>((resolve, reject) => {
            if (!directions) {
                reject('No directions');
                return;
            }
            this.gLoader.loadStreetViewService().subscribe(streetViewService => {
                const streetViewServiceCallback = (data: google.maps.StreetViewPanoramaData | null, status: google.maps.StreetViewStatusString) => {
                    if (status !== google.maps.StreetViewStatus.OK || !data || !data.location || !data.location.latLng) {
                        return reject(status);
                    }
                    const [lng, lat] = this.center().coordinates;
                    const center = new google.maps.LatLng(lat, lng);
                    const heading = google.maps.geometry.spherical.computeHeading(data.location.latLng, center);
                    const pano = data.location.pano;
                    return resolve({ pano, heading });
                };

                streetViewService.getPanorama({ location: directions.location, radius: 50 }, streetViewServiceCallback);
            });
        });

        this.gStreetViewImageService.getDirections(this.center(), this.fullAddress()).pipe(
            mergeMap(directions => from(createPanorama(directions))),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: ({ pano, heading }) => {
                this.pano = new google.maps.StreetViewPanorama(
                    this.panoEl().nativeElement,
                    {
                        addressControl: false,
                        pano,
                        pov: { heading, pitch: 2 },
                    }
                );
                this.pano.addListener('pov_changed', () => {
                    this.panoramaChange.emit();
                });
            },
            error: _ => {
                this.showError.set(true);
            },
        });
    }

    getStaticUrl(width: number, height: number) {
        if (this.showError() || !this.pano) {
            return null;
        }
        const position = this.pano.getPosition();
        if (!position) {
            return null;
        }
        const location = position.lat() + ',' + position.lng();
        const { heading, pitch } = this.pano.getPov();
        const fov = 180 / Math.pow(2, this.pano.getZoom());

        const params = `location=${location}&heading=${heading}&pitch=${pitch}&fov=${fov}&size=${width}x${height}`;
        return `https://maps.googleapis.com/maps/api/streetview?${params}&key=${this.googleApiKey}`;
    }
}
